import type { LoadedAppShellData } from 'src/components/layout/app-shell/use-app-shell-data';
import { isLng } from 'src/utils/is-lng';

/**
 * It can sometimes be hard to know _why_ a navigation item is shown or hidden.
 * And depending on the use case we might want to render the navigation items
 * differently.
 *
 * That's why we collect the conditions here in one place.
 */
export function useNavigationItems({
  cognitoUser,
  monolithUser,
  isSnamAdmin,
  assignmentService,
  capacityManagementService,
  organisationService,
  authorizationService,
  directFcfsService,
  regularFcfsService,
  hydrogenFcfsService,
  remitReportingService,
  mapPrototype,
  documentExchange,
  systemInfo,
  transparencyReportingAuthorization,
  gdmFlags,
  lngMarketingRoot,
}: LoadedAppShellData) {
  // Whoever has the "assignedUsers" link is allowed to see "Shippers",
  // BUT if the user is known the the monolith and _can_ switch to the SSO or TSO role,
  // they should switch to their operator role to see it.
  const shippersNav =
    assignmentService?.response.data._links.assignedUsers &&
    monolithUser?.canSwitchToOrganisationRole !== 'SSO' &&
    monolithUser?.canSwitchToOrganisationRole !== 'TSO';

  const operatorsNav =
    monolithUser?.currentlyActingWithOrganisationRole === 'SHIPPER';

  return {
    topNav: {
      guestDashboard: !cognitoUser && !monolithUser,
      tsoDashboard: cognitoUser && monolithUser?.isTso,
      commonDashboard: cognitoUser && !monolithUser?.isTso,
      timeline: {
        notificationsNew: cognitoUser,
        notificationsOld: monolithUser?.isTso,
        shipperActivities: monolithUser?.isShipper,
      },
      marketMessages: {
        urgentMarketMessages: true,
        maintenanceMessages: true,
      },
      shippers: {
        organisationsAndUsers:
          shippersNav && assignmentService.response.data._links.assignedUsers,
        documents:
          shippersNav &&
          documentExchange?._links.getOperatorDocumentsOutbox?.href,
        invoices: shippersNav && capacityManagementService?._links?.invoicesV2,
        creditLimits: shippersNav && monolithUser?.isOperator,
      },
      operators: {
        assignments: operatorsNav,
        documents: operatorsNav,
        invoices:
          operatorsNav && capacityManagementService?._links?.myInvoicesV2,
      },
      uploads: monolithUser?.isTso,
      hydrogen: !!hydrogenFcfsService,
    },

    transport: {
      auctions: !monolithUser?.isSso,
      fcfsBooking: !monolithUser?.isSso,
      secondaryTrading: !monolithUser?.isSso,
      networkPoints: !monolithUser?.isSso,
      bundleRequests: monolithUser?.isTso,
      multiYearAuctions:
        systemInfo.featureMultiYearCapacity && !monolithUser?.isSso,
      products:
        monolithUser?.isTso ||
        capacityManagementService?._links?.draftedProducts?.href,
      snamBidCollector: isSnamAdmin,
      manageDirectFcfs:
        directFcfsService?._links?.['view-requests'] ||
        directFcfsService?._links?.['read-product'] ||
        directFcfsService?._links?.['read-capacity'],
    },

    storage: {
      offers: true,
      fcfsBooking: monolithUser?.isShipper,
      locations: true,
    },

    lng: {
      offers: true,
      allocations:
        lngMarketingRoot?.response.data._links.getAllocationsOverviewForLso ||
        lngMarketingRoot?.response.data._links.getAllocationsOverviewForShipper,
    },

    hydrogen: {
      booking: !!hydrogenFcfsService?._links?.bookingNetworkPoints,
      shipperBookings: !!hydrogenFcfsService?._links?.bookingOverviewShipper,
      operatorBookings: !!hydrogenFcfsService?._links?.bookingOverviewOperator,
      interactingUsersOverview:
        !!hydrogenFcfsService?._links?.interactingUsersOverview,
      networkPoints: !!hydrogenFcfsService?._links?.networkPointOverview,
    },

    reporting: {
      myTransactions:
        monolithUser?.currentlyActingWithOrganisationRole === 'SHIPPER' &&
        !!capacityManagementService?._links?.myTransactionsV3,
      standardReports: true,
      auctions: true,
      secondaryTrading: true,
      lng: true,
      shipperTransactions:
        monolithUser?.currentlyActingWithOrganisationRole === 'TSO' &&
        !!capacityManagementService?._links?.myTsoTransactionsV3,
      monthlyStatements:
        monolithUser?.currentlyActingWithOrganisationRole === 'TSO' &&
        !!(
          capacityManagementService?._links?.monthlyStatements ||
          capacityManagementService?._links
            ?.monthlyStatementsWithCommodityFeeSupport
        ),
      balancingGroupAllocations:
        monolithUser?.currentlyActingWithOrganisationRole === 'TSO' &&
        !!capacityManagementService?._links?.balancingGroupAllocations,
      tr: transparencyReportingAuthorization?.canView === true,
      remitReports:
        remitReportingService?._links?.['find-primary-reports'] ||
        remitReportingService?._links?.['find-tso-supplied-reports'],
      surrenders: monolithUser?.isShipper,
    },

    gdm: {
      timeSeries: gdmFlags.userCanAcces,
      verifications: gdmFlags.userCanAcces,
      reports: gdmFlags.userCanAcces,
    },

    mapPrototype: mapPrototype?.response,

    prisma: {
      helpAndSupportPortal: true,
      marketInformation: true,
      exchangeRates: true,
    },

    organisationNav: {
      organisationSettings: {
        information:
          !!organisationService?.response.data._links.getMyOrganisationDetails,
        userManagement:
          !!organisationService?.response.data._links.getUserDetails,
        permissionGroups:
          !!authorizationService?.response.data._links.permissionGroups,
        contacts: !!organisationService?.response.data._links.contacts,
        invoiceSettings: !!capacityManagementService?._links?.settingsInvoicing,
        premiumServicesSso: monolithUser?.isSso,
        premiumServicesShipper:
          monolithUser?.role === 'SHIPPER_ADMIN' ||
          !!remitReportingService?._links?.['secondary-settings-details'],
      },
      transportSettings: {
        general: monolithUser?.isTso,
        auctions: monolithUser?.isTso,
        secondaryTradingTso: monolithUser?.isTso,
        secondaryTradingShipper: monolithUser?.isShipper,
        surrender: monolithUser?.isTso,
        regularFcfs: !!regularFcfsService?._links?.['operator-settings'],
        directFcfs: !!directFcfsService?._links?.['read-configuration'],
        remitReporting:
          !!remitReportingService?._links?.['exclude-network-points'] ||
          !!remitReportingService?._links?.['primary-settings-details'],
      },
      storageSettings: {
        general: monolithUser?.isSso && !isLng(monolithUser),
      },
      lngSettings: {
        general: monolithUser?.isSso && isLng(monolithUser),
      },
      reportingSettings: {
        standardReports: monolithUser?.role === 'SHIPPER_ADMIN',
      },
      shipperSettings: {
        assignmentSettings:
          !!assignmentService?.response.data._links.assignmentSettings,
        creditLimits: monolithUser?.isTso,
      },
    },

    userNav: {
      profile: true,
      notificationSettings: true,
      viewSettings: monolithUser?.isShipper,
      dashboardSettings: monolithUser?.isShipper,
      networkPoints: monolithUser?.isShipper,
      bookingAuthorisation: monolithUser?.isShipper,
      switchToShipper: monolithUser?.canSwitchToOrganisationRole === 'SHIPPER',
      switchToSso: monolithUser?.canSwitchToOrganisationRole === 'SSO',
      switchToTso: monolithUser?.canSwitchToOrganisationRole === 'TSO',
      shipperRolePending:
        monolithUser?.isOperator &&
        monolithUser.hasShipperRole &&
        monolithUser.hasAwaitingShipperRole,
      switchAccount: true,
      logout: true,
    },
  };
}
